import React, { useEffect, useMemo, useState } from 'react';
import { PackageIcon, DownloadIcon } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Product, ProductSale } from '../../types';
import { formatCurrency, getToday } from '../../utils/helpers';
import { useAuth } from '../../contexts/AuthContext';
export function DashboardProductRevenue() {
  const {
    user
  } = useAuth();
  const userRoleNorm = user?.role?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';
  const [products, setProducts] = useState<Product[]>([]);
  const [sales, setSales] = useState<ProductSale[]>([]);
  const [dateFrom, setDateFrom] = useState<string>(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState<string>(getToday());
  useEffect(() => {
    try {
      const savedProducts = localStorage.getItem('savidna-products');
      if (savedProducts) setProducts(JSON.parse(savedProducts));
      const savedSales = localStorage.getItem('savidna-product-sales');
      if (savedSales) setSales(JSON.parse(savedSales));
    } catch (e) {
      console.error('Error loading products data:', e);
    }
  }, []);
  const productStats = useMemo(() => {
    const filteredSales = sales.filter(s => {
      return (!dateFrom || s.date >= dateFrom) && (!dateTo || s.date <= dateTo);
    });
    const revenueMap: Record<string, {
      name: string;
      amount: number;
      quantity: number;
    }> = {};
    filteredSales.forEach(sale => {
      sale.items.forEach(item => {
        const product = products.find(p => p.id === item.productId);
        const name = product ? product.name : 'Producto Desconocido';
        if (!revenueMap[item.productId]) {
          revenueMap[item.productId] = {
            name,
            amount: 0,
            quantity: 0
          };
        }
        revenueMap[item.productId].amount += item.subtotal;
        revenueMap[item.productId].quantity += item.quantity;
      });
    });
    return Object.values(revenueMap).sort((a, b) => b.amount - a.amount);
  }, [sales, products, dateFrom, dateTo]);
  const totalRevenue = productStats.reduce((sum, item) => sum + item.amount, 0);
  return <Card padding="none" className="overflow-hidden" data-id="element-1909">
      <div className="h-1.5 bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500" data-id="element-1910" />
      <div className="p-4 md:p-6" data-id="element-1911">
        <CardHeader className="px-0 pt-0 flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-1912">
          <CardTitle className="flex items-center gap-2" data-id="element-1913">
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 shadow-lg shadow-amber-500/20" data-id="element-1914">
              <PackageIcon className="w-5 h-5 text-white" data-id="element-1915" />
            </div>
            <div data-id="element-1916">
              <span className="block" data-id="element-1917">Ingresos por Productos</span>
              <span className="text-xs font-normal text-gray-500 dark:text-gray-400" data-id="element-1918">
                Ventas realizadas
              </span>
            </div>
          </CardTitle>
          <div className="flex items-center gap-2" data-id="element-1919">
            <Badge className="bg-gradient-to-r from-amber-500 to-orange-600 text-white py-1.5 px-3 text-sm shadow-lg shadow-amber-500/20 border-none" data-id="element-1920">
              {formatCurrency(totalRevenue)}
            </Badge>
          </div>
        </CardHeader>

        <div className="flex flex-wrap items-center gap-2 mt-4 mb-5" data-id="element-1921">
          <div className="flex items-center gap-1.5 bg-gray-50 dark:bg-gray-800 p-1 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-1922">
            <input type="date" className="bg-transparent text-xs border-none focus:ring-0 p-1 text-gray-700 dark:text-gray-300" value={dateFrom} onChange={e => setDateFrom(e.target.value)} data-id="element-1923" />
            <span className="text-gray-300 text-xs" data-id="element-1924">→</span>
            <input type="date" className="bg-transparent text-xs border-none focus:ring-0 p-1 text-gray-700 dark:text-gray-300" value={dateTo} onChange={e => setDateTo(e.target.value)} data-id="element-1925" />
          </div>
        </div>

        <CardContent className="px-0 pb-0" data-id="element-1926">
          {productStats.length > 0 && <div className="mb-6" data-id="element-1927">
              <ResponsiveContainer width="100%" height={240} data-id="element-1928">
                <BarChart data={productStats.slice(0, 6)} layout="vertical" margin={{
              left: 0,
              right: 10
            }} data-id="element-1929">
                  <defs data-id="element-1930">
                    <linearGradient id="productRevenueGrad" x1="0" y1="0" x2="1" y2="0" data-id="element-1931">
                      <stop offset="0%" stopColor="#f59e0b" data-id="element-1932" />
                      <stop offset="100%" stopColor="#ea580c" data-id="element-1933" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={true} vertical={false} data-id="element-1934" />
                  <XAxis type="number" stroke="#9ca3af" fontSize={11} tickLine={false} axisLine={false} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} data-id="element-1935" />
                  <YAxis dataKey="name" type="category" stroke="#9ca3af" fontSize={12} tickLine={false} axisLine={false} width={120} tick={{
                fill: '#374151',
                fontWeight: 500
              }} data-id="element-1936" />
                  <Tooltip contentStyle={{
                backgroundColor: 'white',
                border: 'none',
                borderRadius: '12px',
                boxShadow: '0 10px 40px rgba(0,0,0,0.12)',
                padding: '10px 14px'
              }} formatter={(value: number) => [formatCurrency(value), 'Ingresos']} data-id="element-1937" />
                  <Bar dataKey="amount" fill="url(#productRevenueGrad)" radius={[0, 6, 6, 0]} name="Ingresos" data-id="element-1938" />
                </BarChart>
              </ResponsiveContainer>
            </div>}
          <div className="space-y-4" data-id="element-1939">
            {productStats.map((product, index) => {
            const maxAmount = Math.max(...productStats.map(p => p.amount), 1);
            const medals = ['🥇', '🥈', '🥉'];
            const medal = index < 3 ? medals[index] : null;
            return <div key={product.name} className="flex items-center gap-3 px-1 group" data-id="element-1940">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-sm ${index === 0 ? 'bg-gradient-to-br from-amber-400 to-yellow-500 text-white' : index === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-400 text-white' : index === 2 ? 'bg-gradient-to-br from-orange-400 to-amber-500 text-white' : 'bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 border border-orange-100 dark:border-orange-800'}`} data-id="element-1941">
                    {medal || index + 1}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-1942">
                    <div className="flex justify-between mb-1.5" data-id="element-1943">
                      <div className="flex items-center gap-2 min-w-0" data-id="element-1944">
                        <span className="text-[15px] font-semibold text-gray-800 dark:text-gray-200 truncate" data-id="element-1945">
                          {product.name}
                        </span>
                        <span className="text-[11px] text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded-full whitespace-nowrap flex-shrink-0" data-id="element-1946">
                          {product.quantity} unid.
                        </span>
                      </div>
                      <span className="text-[15px] font-bold text-orange-600 dark:text-orange-400 ml-2 whitespace-nowrap" data-id="element-1947">
                        {formatCurrency(product.amount)}
                      </span>
                    </div>
                    <div className="w-full bg-gray-100 dark:bg-gray-800 rounded-full h-2.5 overflow-hidden" data-id="element-1948">
                      <div className="h-full rounded-full transition-all duration-1000 ease-out bg-gradient-to-r from-amber-500 to-orange-500" style={{
                    width: `${product.amount / maxAmount * 100}%`
                  }} data-id="element-1949" />
                    </div>
                  </div>
                </div>;
          })}
            {productStats.length === 0 && <div className="text-center py-10 text-gray-400 text-sm italic" data-id="element-1950">
                No se encontraron ventas en este período.
              </div>}
          </div>
        </CardContent>
      </div>
    </Card>;
}